import { useState, useCallback, useMemo, useEffect } from "react";
import useAuth from "../../hooks/useAuth";
import gtm from "../../lib/gtm";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Typography,
  Link,
  LinearProgress,
  Divider,
} from "@mui/material";
import PasswordInput from "@rlmonger/web_components/dist/Inputs/Password";
import { Wrapper } from "src/components/authentication";
import { Link as RouterLink } from "react-router-dom-v5-compat";
import axios from "axios";
import useSettings from "src/hooks/useSettings";
import Account from "../dashboard/Account";

const ForgotPassword = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);

  const { settings: config } = useSettings();

  const [forgotEmail, setForgotEmail] = useState("");
  const valid = useMemo(
    () => forgotEmail.length > 0 && /^.+@.+\..+$/.test(forgotEmail) && !loading,
    [forgotEmail, loading]
  );

  const getRecoveryEmail = async () => {
    setLoading(true);

    const { status } = await axios.post(`${config.api}/auth/forgotPassword`, {
      email: forgotEmail,
      platform: config.platform, // MongerTech origin
    });

    setForgotEmail("");

    setLoading(false);
    onClose(status === 200);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Forgot Password</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter your email below to recieve a link to reset your password.
        </DialogContentText>
        <TextField
          sx={{ mt: 2 }}
          label="Email"
          onChange={(e) => setForgotEmail(e.target.value)}
          value={forgotEmail}
          type="email"
          fullWidth
        />
        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={getRecoveryEmail}
          variant="contained"
          disabled={!valid}
        >
          Recover Password
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Login = () => {
  const [alert, setAlert] = useState({ severity: "success", message: "" });

  useEffect(() => {
    gtm.push({ event: "page_view" });
  }, []);

  const { login } = useAuth();

  const [modalOpen, setModalOpen] = useState(false);

  const onModalClose = useCallback((success) => {
    setModalOpen(false);

    if (success === true)
      setAlert({
        severity: "success",
        message: "Password reset email sent successfully.",
      });
    else if (success === false)
      setAlert({
        severity: "error",
        message:
          "Something went wrong whilst requesting a password reset. Email address might not be associated with an account.",
      });
    else setAlert(null);
  }, []);

  return (
    <Wrapper
      title="Login"
      alert={alert}
      description={
        <>
          Log in to our tracking platform.
          <br />
          Don't have an account?{" "}
          <Link to="/register" component={RouterLink}>
            Create an account
          </Link>
        </>
      }
    >
      <Formik
        initialValues={{
          email: "",
          password: "",
          submit: null,
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("Must be a valid email")
            .max(255)
            .required("Email is required"),
          password: Yup.string().max(255).required("Password is required"),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            await login(values.email, values.password);

            setStatus({ success: true });
            setSubmitting(false);
          } catch (err) {
            setAlert({
              severity: "error",
              message: err.message,
            });

            setStatus({ success: false });
            setErrors({ submit: err.message });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Box sx={{ mt: 2 }}>
              <TextField
                autoFocus
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                label="Email Address"
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                type="email"
                value={values.email}
                variant="outlined"
              />
            </Box>
            <Box
              sx={{
                mt: 2,
                textAlign: "right",
              }}
            >
              <PasswordInput
                formControlStyle={{
                  width: `100%`,
                }}
                style={{ width: `100%` }}
                error={Boolean(touched.password && errors.password)}
                fullWidth
                label="Password"
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.password}
                variant="outlined"
              />
              <Typography variant="caption">
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setModalOpen(true);
                  }}
                >
                  Forgot your password?
                </Link>
              </Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button
                color="primary"
                disabled={isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                Log In
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mt: 2 }}>
              <Button
                color="primary"
                component={Link}
                fullWidth
                size="large"
                href="https://app.monger.tech/admin_login?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZV9pZCI6NjQxLCJlbWFpbCI6ImRlbW9AbW9uZ2VyLnRlY2giLCJmaXJzdF9uYW1lIjoiRGVtbyIsImxhc3RfbmFtZSI6Ik1vbmdlciIsInN0cmVldCI6bnVsbCwiY2l0eSI6bnVsbCwic3RhdGUiOm51bGwsInBhc3N3b3JkIjoiJDJiJDEwJHRBOXEycWpSYnhyaEhxMFBYZmZZMk9LWlRMU1dQOW1rRVpScVFWdVpCM3lwMFBuNjlLNXFLIiwibW9iaWxlIjpudWxsLCJzbXNfYWxlcnRzIjpudWxsLCJlbWFpbF9hbGVydHMiOm51bGwsImNyZWF0ZWRfb24iOm51bGwsImxhc3RfbG9naW4iOm51bGwsImltZWlzIjpudWxsLCJzdHJpcGVpZCI6bnVsbCwiaW1wZXJpYWwiOm51bGwsImVtcGxveWVlIjpmYWxzZSwiZmlyZWJhc2VkdG9rZW4iOiJlM1BqQjhwdDBVWVJtNW55bldOQmdsOkFQQTkxYkc0bFliLW9ZZUNiMWNLUlJCSW9waGNMWW9udXoyQjdxbFRQX3FKWE01SnloTnFUNTZtem5KTVNjRS1FNkVQN1ZjTE9yRURJUG1SUHkzNWhRRzlISUlfem1KaC13Rm41UGlFMXNpMlJwZjFBZml3YllUX2YwalJhemFzdmptaEJucWtuWWNKIiwiZ3JvdXBzIjpbXSwiY29tcGFueWlkIjoxMjksInN1YmNvbnRyYWN0Y29tcGFueSI6bnVsbCwiaG1hZG1pbiI6bnVsbCwidHNhZG1pbiI6bnVsbCwidm1hZG1pbiI6bnVsbCwibGFib3VyaWQiOm51bGwsIm90cCI6bnVsbCwiam9ic2l0ZXMiOltdLCJwaW4iOjE5OTgsInBlcm1pc3Npb25ncm91cHMiOltdLCJ0ZXJtc19hY2NlcHRlZCI6bnVsbCwidGltZXNoZWV0ZmJ0b2tlbiI6bnVsbCwiYWN0aXZlIjp0cnVlLCJwb3N0Y29kZSI6bnVsbCwidGFncyI6W10sImFwcHJvdmFsbGV2ZWwiOjAsInBob3RvIjpudWxsLCJzY2hlZHVsZWRfc2hpZnRzIjpudWxsLCJ3aGF0c25ldyI6bnVsbCwicGF5cm9sbGdyb3VwaWQiOm51bGwsImFzc2lnbmVkX2pvYnNpdGUiOm51bGwsInJvbGVzIjpudWxsLCJyZWFkX25vdGlmaWNhdGlvbnMiOm51bGwsImhpZGRlbiI6bnVsbCwiZHJpdmVyIjpudWxsLCJzZXR0aW5ncyI6bnVsbCwibmV4dF9vZl9raW4iOm51bGwsIm5leHRfb2Zfa2luX3JlbGF0aW9uc2hpcCI6bnVsbCwibmV4dF9vZl9raW5fbnVtYmVyIjpudWxsLCJlbWVyZ2VuY3lfY29udGFjdCI6bnVsbCwiZW1lcmdlbmN5X2NvbnRhY3RfcmVsYXRpb25zaGlwIjpudWxsLCJlbWVyZ2VuY3lfY29udGFjdF9udW1iZXIiOm51bGwsInVzaSI6bnVsbCwiZG9iIjpudWxsLCJkYXRlb2ZiaXJ0aCI6bnVsbCwicGxhdGZvcm1pZCI6bnVsbCwiaWF0IjoxNzIzNTMxMDg4fQ.GcYQR9W_S-7wDv2aXCHCuVvfH2VEbrg19vK3X7NOwh0"
                variant="outlined"
              >
                Demo Account
              </Button>
            </Box>
          </form>
        )}
      </Formik>

      <ForgotPassword open={modalOpen} onClose={onModalClose} />
    </Wrapper>
  );
};

export default Login;
